import { useState } from "react";
import { api } from "../../api/client";
import { useNotify } from "../../components/notifications";
import { AppDialog } from "../../components/AppDialog";
import type { CrudField } from "./moduleCrud";

interface Option {
  id: string;
  name: string;
}

type QuickAddSpec = NonNullable<CrudField["quickAdd"]>;

interface Props {
  /** The parent select field's `quickAdd` config (createUrl, title, fields, …). */
  spec: QuickAddSpec;
  /** The parent select's `optionsUrl` — re-read after create to pick up the fresh
   *  {id, name} the server assigns, so the new row is selected by its real id. */
  optionsUrl: string;
  onClose: () => void;
  /** Fresh option list + the id of the newly-created row to select. */
  onAdded: (options: Option[], selectedId: string) => void;
}

/**
 * Inline "add new" popup for a select field (legacy XAF grid-lookup "New" button
 * parity — e.g. Task Checklist → Category / Ownership). It POSTs to the lookup
 * master's OWN endpoint (no new backend), then re-reads the parent select's option
 * list and hands back the freshly-created row's id to select.
 *
 * <para>The create is audited by the master's own controller (Update/Delete +
 * Create via the global CreatedAuditFilter — §18.15 C3 Utility-master reversal),
 * so this popup adds no audit code of its own.</para>
 */
export function QuickAddDialog({ spec, optionsUrl, onClose, onAdded }: Props) {
  const notify = useNotify();
  // Opened from a square-control form (every Settings ▸ Utility form): the popup boxes
  // read square + white too (owner request 2026-10-13). Read once on open.
  const [square] = useState(() => !!document.querySelector(".sd-grid--square"));
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(spec.fields.map((f) => [f.key, ""])),
  );
  const [saving, setSaving] = useState(false);
  const [triedSave, setTriedSave] = useState(false);

  // A "readonly" field only shows a value the server will fill in (e.g. "Autogenerate") — it is never sent.
  const isShown = (f: CrudField) => f.type === "readonly";
  const nameKey = spec.nameKey ?? spec.fields.find((f) => !isShown(f))?.key ?? "";
  const hasMissingRequired = spec.fields.some((f) => f.required && !values[f.key]?.trim());

  const submit = async () => {
    setTriedSave(true);
    if (hasMissingRequired || saving) return;
    setSaving(true);
    try {
      const payload: Record<string, string> = {};
      for (const f of spec.fields) if (!isShown(f)) payload[f.key] = (values[f.key] ?? "").trim();
      await api.post(spec.createUrl, payload);

      // Re-read the parent select's options so the new row appears with the id +
      // display text the SERVER assigns; select it by the name we just entered
      // (these masters enforce unique names, so the match is unambiguous).
      const res = await api.get<{ data?: Option[] }>(optionsUrl);
      const opts = res.data?.data ?? [];
      const enteredName = (values[nameKey] ?? "").trim().toLowerCase();
      const match = opts.find((o) => (o.name ?? "").trim().toLowerCase() === enteredName);

      onAdded(opts, match?.id ?? "");
      notify.success("Saved successfully.");
      onClose();
    } catch (e) {
      // The master endpoints return a friendly validation message (e.g. a duplicate
      // name); surface it rather than a generic failure.
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
        `Could not add the ${spec.title.toLowerCase()}.`;
      notify.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppDialog
      open
      title={spec.title}
      icon={spec.icon}
      width={460}
      onClose={onClose}
      footer={
        spec.okFirst ? (
          // Popup standard (spec.okFirst): OK first, then Cancel.
          <>
            <button type="button" className="ca-btn ca-btn-solid" onClick={submit} disabled={saving}>
              OK
            </button>
            <button type="button" className="ca-btn" onClick={onClose} disabled={saving}>
              <span className="tb-i tb-i-grey">↩︎</span> Cancel
            </button>
          </>
        ) : (
          <>
            <button type="button" className="ca-btn" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="ca-btn ca-btn-solid" onClick={submit} disabled={saving}>
              OK
            </button>
          </>
        )
      }
    >
      <div className={`qs-grid-wide${square ? " qs-grid--square" : ""}`}>
        {spec.fields.map((f) => {
          const empty = triedSave && !!f.required && !values[f.key]?.trim();
          const firstEditable = f.key === spec.fields.find((x) => !isShown(x))?.key;
          return (
            <div className="qs-field" key={f.key}>
              <label className="qs-label" htmlFor={`qa-${f.key}`}>
                {/* Colon (ink) + red required star, matching the main form's sd-label
                    (owner report 2026-10-12, images 444/445). */}
                {f.label}{f.required ? <>{" :"}<span className="req">*</span></> : ":"}
              </label>
              {isShown(f) ? (
                // Read-only: shows what the server fills in (e.g. System ID "Autogenerate").
                <input id={`qa-${f.key}`} className="qs-input" type="text" value={String(f.defaultValue ?? "")}
                  readOnly disabled tabIndex={-1} />
              ) : f.type === "textarea" ? (
                <textarea
                  id={`qa-${f.key}`}
                  className={`qs-input${empty ? " required-empty" : ""}`}
                  rows={3}
                  value={values[f.key] ?? ""}
                  maxLength={f.maxLength}
                  autoFocus={firstEditable}
                  onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                />
              ) : (
                <input
                  id={`qa-${f.key}`}
                  className={`qs-input${empty ? " required-empty" : ""}`}
                  type="text"
                  value={values[f.key] ?? ""}
                  maxLength={f.maxLength}
                  autoFocus={firstEditable}
                  onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                />
              )}
            </div>
          );
        })}
      </div>
    </AppDialog>
  );
}
